import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import BNMark from '../components/BNMark';
import Enter from './Enter';

/* What someone sees before they're a member: what B/N is, who it's for, how
   it works and why it's different — with sign-in right there, the way the big
   networks greet signed-out visitors. */

const WHO = ['🚀 Founders', '🛠️ Engineers', '🎨 Designers', '📈 Growth & GTM', '💡 Product', '🔬 Researchers', '💰 Angels & VCs', '🌱 Students building'];
const KIND_TAGS = [['Ask', '#F5B83D'], ['Test', '#B58CFF'], ['Offer', '#3DD6A3'], ['Intro', '#4DA3FF'], ['Looking for', '#F2A0C8'], ['Refer', '#3DD6A3']];
const COMPARE = [
  ['Meeting people', 'Cold messages, pay to reach strangers', 'Introductions both sides agree to'],
  ['What you see', 'An endless feed ranked for engagement', 'Requests from people who need help — and get it'],
  ['Who’s there', 'Anyone, bots included', 'Invited builders someone vouches for'],
  ['Your data', 'Used to sell ads and recruiter seats', 'Never sold. You choose who sees you.'],
  ['Pricing', 'Long contracts, locked-in seats', 'Free to join; fair monthly extras, no lock-in'],
];
const FEATURES = [
  ['📋', 'Requests', 'Ask, Test, Offer, Intro, Looking For, Refer — each stays open until it’s solved.'],
  ['🤝', 'Introductions', 'Matched on what you’re building and what you need. Double opt-in.'],
  ['💬', 'Groups & chats', 'Private working groups with topics, files, link previews and quoted replies.'],
  ['✨', 'Copilot', 'An AI assistant that knows your work — drafts, prep briefs, documents.'],
  ['🕸️', 'Network map', 'See how you’re connected — and who’s one intro away.', true],
  ['📱', 'Web & mobile', 'On the web, Android and iPhone. Same account everywhere.'],
];

function RequestInvite({ onClose }) {
  const [f, setF] = useState({ name: '', email: '', link: '', building: '', website: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function send(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try { await api.requestInvite(f); setDone(true); } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  return createPortal(
    <div className="modal-back" role="presentation" onClick={onClose}>
      <div className="modal card pad lp-request" role="dialog" aria-modal="true" aria-label="Request an invite" onClick={(e) => e.stopPropagation()}>
        <div className="modal-top"><h2>{done ? 'Thanks — we’ve got it' : 'Request an invite'}</h2>
          <button className="linklike" aria-label="Close" onClick={onClose}>×</button></div>
        {done ? (
          <p className="muted-sm">We review every request personally. If it’s a fit, your invite arrives by email.</p>
        ) : (
          <form onSubmit={send}>
            <p className="muted-sm">B/N stays small so every member gets real introductions. Tell us a little about you.</p>
            <label className="flabel" htmlFor="rq-name">Your name</label>
            <input id="rq-name" className="in" value={f.name} onChange={set('name')} autoComplete="name" required minLength={2} />
            <label className="flabel" htmlFor="rq-email">Email</label>
            <input id="rq-email" className="in" type="email" value={f.email} onChange={set('email')} autoComplete="email" required />
            <label className="flabel" htmlFor="rq-link">Your X, LinkedIn or site</label>
            <input id="rq-link" className="in" value={f.link} onChange={set('link')} placeholder="https://x.com/you" />
            <label className="flabel" htmlFor="rq-building">What are you building?</label>
            <textarea id="rq-building" className="ta" rows={3} value={f.building} onChange={set('building')} maxLength={1000} />
            <input className="lp-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set('website')} />
            {error && <div className="enter-error">{error}</div>}
            <button className="btn apply" type="submit" disabled={busy || f.name.trim().length < 2 || !f.email.trim()}>
              {busy ? 'Sending…' : 'Request an invite'}
            </button>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default function Landing({ onEntered }) {
  const [stats, setStats] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [joinKey, setJoinKey] = useState(0);
  useEffect(() => { api.publicStats().then(setStats).catch(() => {}); }, []);
  const toSignIn = (join) => {
    if (join) setJoinKey(Date.now());
    document.getElementById('sign-in')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const nums = stats ? [['members', 'members on B/N'], ['introductions', 'introductions made'], ['asks_resolved', 'asks resolved']]
    .filter(([k]) => stats[k] != null) : [];
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="landing">
      <div className="lp-wrap">
        <nav className="lp-nav">
          <BNMark size={44} hub />
          <span className="lp-name">Builders' Network</span>
          <div className="lp-links">
            <button type="button" onClick={() => go('how')}>How it works</button>
            <button type="button" onClick={() => go('who')}>Who it’s for</button>
            <button type="button" onClick={() => go('why')}>Why B/N</button>
            <button type="button" className="btn ghost lp-signin-btn" onClick={() => toSignIn(false)}>Sign in</button>
          </div>
        </nav>

        <header className="lp-hero">
          <div>
            <span className="lp-eyebrow">● Invite-only · AI &amp; Web3 builders</span>
            <h1>Meet the people who <em>move your work forward.</em></h1>
            <p className="lp-sub">Say what you’re building and what you need. B/N introduces you to the few people worth meeting —
              testers, co-founders, hires, customers, investors — and only when both of you say yes.</p>
            {nums.length > 0 && (
              <div className="lp-proof">{nums.map(([k, l]) => <div key={k}><b>{stats[k]}</b><span>{l}</span></div>)}</div>
            )}
            <div className="lp-viz" aria-hidden="true">
              <svg viewBox="0 0 620 300">
                <g stroke="rgba(77,163,255,.55)" strokeWidth="2"><line x1="90" y1="150" x2="250" y2="80" /><line x1="250" y1="80" x2="420" y2="130" />
                  <line x1="250" y1="80" x2="300" y2="230" /><line x1="420" y1="130" x2="560" y2="70" /><line x1="300" y1="230" x2="470" y2="250" /></g>
                <line x1="420" y1="130" x2="470" y2="250" stroke="#B58CFF" strokeWidth="2" strokeDasharray="6 5" />
                <g fill="#1450C8" stroke="#9CCBFF" strokeWidth="2"><circle cx="90" cy="150" r="20" /><circle cx="250" cy="80" r="26" /><circle cx="420" cy="130" r="22" />
                  <circle cx="300" cy="230" r="18" /><circle cx="560" cy="70" r="16" /><circle cx="470" cy="250" r="20" /></g>
              </svg>
              <span className="lp-pill" style={{ left: '47%', top: '2%' }}>🔍 Looking for: <b>5 beta testers</b></span>
              <span className="lp-pill" style={{ left: '71%', top: '57%' }}>🤝 Intro — both said yes</span>
              <span className="lp-pill" style={{ left: '3%', top: '77%' }}>✓ Ask resolved in <b>2 days</b></span>
            </div>
          </div>
          <div>
            <Enter onEntered={onEntered} embedded joinKey={joinKey} />
            <p className="lp-noinvite">No invite yet? <button type="button" className="linklike" onClick={() => setRequesting(true)}>Request one →</button></p>
          </div>
        </header>

        <section id="who" className="lp-sec">
          <div className="lp-kicker">Who it’s for</div>
          <h2>Built for people who build in AI and Web3</h2>
          <p className="lp-lead">Small on purpose. Every member is invited by someone already here, so the people you meet are people someone vouches for.</p>
          <div className="lp-chips">{WHO.map((w) => <span key={w} className="lp-chip">{w}</span>)}</div>
        </section>

        <section id="how" className="lp-sec">
          <div className="lp-kicker">How it works</div>
          <h2>Three steps, no noise</h2>
          <p className="lp-lead">No feed algorithm deciding who sees you. You say what you need; the network helps you get it.</p>
          <div className="lp-steps">
            <div className="card lp-step"><span className="lp-num">1</span><h3>Say what you need</h3>
              <p>Post what you’re working on, or ask for something specific — feedback, testers, a hire, an intro, funding.</p>
              <div className="lp-tags">{KIND_TAGS.map(([t, col]) => <span key={t} style={{ color: col, borderColor: col }}>{t}</span>)}</div></div>
            <div className="card lp-step"><span className="lp-num">2</span><h3>Get introduced</h3>
              <p>B/N finds the few people who fit and introduces you. You’re only connected when <b>both of you say yes</b> — no cold pitches, ever.</p></div>
            <div className="card lp-step"><span className="lp-num">3</span><h3>Get it done</h3>
              <p>Message, work together in private groups with files and topics, and mark your ask solved. Copilot helps you prepare, draft and follow up.</p></div>
          </div>
        </section>

        <section id="why" className="lp-sec">
          <div className="lp-kicker">Why B/N</div>
          <h2>Not another feed</h2>
          <p className="lp-lead">The big networks are built to keep you scrolling and to sell your attention. B/N is built to get things done.</p>
          <div className="card lp-cmp">
            <table>
              <thead><tr><th aria-label="Topic" /><th>The big networks</th><th>Builders’ Network</th></tr></thead>
              <tbody>{COMPARE.map(([a, b, c]) => <tr key={a}><td><b>{a}</b></td><td>{b}</td><td>{c}</td></tr>)}</tbody>
            </table>
          </div>
        </section>

        <section className="lp-sec">
          <div className="lp-kicker">Inside B/N</div>
          <h2>Everything a builder needs to move</h2>
          <div className="lp-feats">
            {FEATURES.map(([ic, t, d, beta]) => (
              <div key={t} className="card lp-feat"><div className="lp-ic" aria-hidden="true">{ic}</div>
                <h3>{t}{beta && <em className="beta-tag">Beta</em>}</h3><p>{d}</p></div>
            ))}
          </div>
        </section>

        <section className="lp-sec">
          <div className="card lp-final">
            <h2>B/N is invite-only, on purpose.</h2>
            <p className="lp-lead">Every member gets real introductions because the network stays small and trusted. Ask a member for an invite, or request one and we’ll review it personally.</p>
            <div className="lp-row">
              <button type="button" className="btn apply" onClick={() => setRequesting(true)}>Request an invite</button>
              <button type="button" className="btn ghost" onClick={() => toSignIn(true)}>I have an invite code</button>
            </div>
          </div>
        </section>

        <footer className="lp-foot">
          <span>© {new Date().getFullYear()} Builders' Network</span>
          <a href="/terms">Terms</a><a href="/privacy">Privacy</a>
        </footer>
      </div>
      {requesting && <RequestInvite onClose={() => setRequesting(false)} />}
    </div>
  );
}
